<template>
  <v-dialog v-model="dialog" persistent :width="500">
    <AlFormCard title="Submit Chit" subtitle="Fill in the details to submit the chit">
      <template #error>
        <ErrorAlert
          v-if="submitError != null"
          :error="submitError"
          @clearErrors="clearErrors()"
          class="mb-0 rounded"
        />
      </template>

      <template #appendAction>
        <v-btn
          variant="tonal"
          class="rounded"
          size="32"
          @click="closeDialog()"
          icon
          color="default"
          density="comfortable"
        >
          <v-icon size="18" icon="mdi-close" />
        </v-btn>
      </template>

      <v-form ref="form" class="mt-4">
        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model="submitForm.by"
              label="Cleared By"
              variant="outlined"
              hide-details="auto"
              density="compact"
              @update:model-value="clearErrors()"
              :rules="[requiredValidator]"
              :error-messages="submitError && (submitError.fieldErrors['by'] as string[])"
            />
          </v-col>
          <v-col cols="12">
            <DateTimePickerField
              v-model="submitForm.departed_at"
              label="Departed At"
              variant="outlined"
              hide-details="auto"
              density="compact"
              @update:model-value="clearErrors()"
              :rules="[requiredValidator]"
              :error-messages="submitError && (submitError.fieldErrors['departed_at'] as string[])"
            ></DateTimePickerField>
          </v-col>
        </v-row>
      </v-form>

      <template #actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" variant="flat" :loading="isSubmitChitPending" @click="submit()"
          >Save</v-btn
        >
      </template>
    </AlFormCard>
  </v-dialog>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue'
import { useSubmitChit } from '@/composables/chit'
import type { IChitSubmitParams } from '@/services'
import { requiredValidator } from '@/utils/validators'
import AlFormCard from '../common/AlFormCard.vue'
import ErrorAlert from '../common/ErrorAlert.vue'
import DateTimePickerField from '../common/DateTimePickerField.vue'

export interface Props {
  chitNumber: number
}
const props = defineProps<Props>()

const emit = defineEmits(['submitted-chit'])

const dialog = defineModel<boolean>('dialog', { required: true })
const submitForm = ref<IChitSubmitParams>({
  chit_number: props.chitNumber,
  by: '',
  departed_at: new Date()
})

watch(dialog, (value) => {
  if (value) {
    clearErrors()

    submitForm.value = {
      chit_number: props.chitNumber,
      by: '',
      departed_at: new Date()
    } as IChitSubmitParams
  }
})

function closeDialog() {
  clearErrors()
  dialog.value = false
}

const {
  isPending: isSubmitChitPending,
  error: submitError,
  reset: submitReset,
  mutate: submitChit
} = useSubmitChit()

function submit() {
  clearErrors()
  submitChit(submitForm.value, {
    onSuccess: () => {
      emit('submitted-chit')
      closeDialog()
    }
  })
}

function clearErrors() {
  submitReset()
}
</script>
